/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/

import React, { useEffect, useState } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { InView } from 'react-intersection-observer';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import ActionTypes from '@ziynx/action-types';
import Card from '@ziynx/components/cards';
import { eventNames } from '@ziynx/analytics';
import Colors from '@ziynx/theme/colors';
import Link from '@ziynx/components/links';
import { Button, ButtonPillGroup } from '@ziynx/components/buttons';
import { ContentContainer, PageTemplate } from '@ziynx/components/layout';
import {
  withAppManager, LoadingSpinner, getGraduationInfo, getInitials,
  useNotification, getTotalFilters, findField,
} from '@ziynx/components/utilities';
import Headline from '@ziynx/components/headlines';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import type { ChannelType as ChannelItemType } from '@ziynx/flow-types/channel';
import type { DiscoverType as DiscoverFromAPI } from '@ziynx/flow-types/discover';
import DiscoverEmptyState from './subcomponents/EmptyState';

type History = {
  push: (pathName: string, state: Object) => void,
};

type Location = {
  state: {
    title?: string
  },
};

type DispatchAction = {
  type: string,
  id?: string,
  page?: number,
} => void;

type ChannelType = {
  isLastPage: boolean,
  success: boolean,
  fetching: boolean,
  error: any,
  _id: string,
  name: string, // section   title
  pageSize: number,
  results: Array<ChannelItemType>,
  hint: {
    timestamp?: number, // API update.
    skipCache?: boolean, // API update
    viewedCount?: number, // API update
  }
}

type DiscoverType = {
  ...DiscoverFromAPI,
  fetching: boolean,
  success: boolean,
  error: any,
};

type Props = $ReadOnly<{||}>;

const LinkWrapper = styled.div`
  margin-bottom: 24px;
`;

const CardWrapper = styled.div`
  ${({ lastChild }) => !lastChild && 'padding-bottom: 64px;'};
`;

const LeftSlotWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 688px;
`;

const RightSlotWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
`;

const FilterWrapper = styled.div`
  padding-bottom: 64px;
`;

const FilterHeadlineWrapper = styled.div`
  padding-bottom: 24px;
`;

const PillWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const HeadlineWrapper = styled.div`
  padding-bottom: 40px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-top: 32px;
`;

const BANNER_MESSAGE = {
  successAdd: 'has been added to your watchlist.',
  successRemove: 'has been removed from your watchlist.',
  error: 'An error occurred. Please try again later.',
  removing: 'Removing member.',
  adding: 'Adding member',
};

const ZIYNX_LOGO = 'https://ziynx-media-assets-dev.s3.amazonaws.com/ziynx_logo/ziynx-logo.png';

const ChannelScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport } = props;
  const history: History = useHistory();
  const location: Location = useLocation();
  const { channelId }: { channelId: string } = useParams();
  const dispatch: DispatchAction = useDispatch();

  const { notificationDispatch } = useNotification();

  const channelService: ChannelType = useSelector(
    ({ channel }) => (channel || {})[channelId] ?? {}, shallowEqual,
  );

  const filterService = useSelector(({ filter }) => filter?.appliedFilters ?? {}, shallowEqual);
  const discoverService: DiscoverType = useSelector(({ discover }) => discover || {}, shallowEqual);
  const watchlistService = useSelector(
    ({ watchlist }) => ({
      ...watchlist[ActionTypes.watchlist.add.GROUP] || {},
      removeUser: watchlist[ActionTypes.watchlist.remove.GROUP] || {},
    }), shallowEqual,
  );

  const [talentName, setTalentName] = useState('');

  const WATCHLIST_TOTAL = discoverService?.watchlist?.total;
  const FILTER_TOTAL = getTotalFilters(Object.values(filterService ?? {})).length;

  const isMobileSmall = viewport === 'mobileSmall';
  const isNonMobile = viewport !== 'mobile' && viewport !== 'mobileSmall';
  const isNonDesktop = viewport !== 'desktop';

  const goToWatchlist = () => {
    history.push('/discover/watchlist', {
      title: location?.state?.title,
      backLink: `/discover/channel/${channelService?._id}`,
    });
  };

  const goToFilter = () => {
    history.push('/discover/filter', {
      title: location?.state?.title,
      backLink: `/discover/channel/${channelService?._id}`,
    });
  };

  const RIGHT_ACTION_LINK_ARRAY = [
    {
      children: FILTER_TOTAL ? `Filter (${FILTER_TOTAL})` : 'Filter',
      onClick: goToFilter,
    },
    {
      children: WATCHLIST_TOTAL ? `Watchlist (${WATCHLIST_TOTAL > 99 ? `${WATCHLIST_TOTAL}+` : WATCHLIST_TOTAL})` : 'Watchlist',
      onClick: goToWatchlist,
    },
  ];

  const resetNotification = (isRemove = false) => {
    if (isRemove) {
      dispatch({
        type: ActionTypes.watchlist.clearRemove,
      });
    } else {
      dispatch({
        type: ActionTypes.watchlist.clearAdd,
      });
    }
  };

  useEffect(() => {
    dispatch({
      type: ActionTypes.discover.getChannel.START,
      id: channelId,
      page: 0,
      ...!!Object.keys(filterService).length && {
        body: { filters: filterService },
      },
    });
  }, [filterService]);

  useEffect(() => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.navigated_see_all_discover,
      properties: {
        channelId,
      },
    });
  }, []);

  /** ADDING TO WATCHLIST HERE */
  useEffect(() => {
    if (watchlistService?.success && !watchlistService?.fetching && talentName) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          content: `${talentName} ${BANNER_MESSAGE?.successAdd}`,
          variant: 'success',
        },
      });
      resetNotification();
    }
    if (watchlistService?.error && !watchlistService?.fetching) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          content: BANNER_MESSAGE.error,
          variant: 'error',
        },
      });
      resetNotification();
    }
  }, [watchlistService?.success,
    watchlistService?.fetching,
    watchlistService?.error,
    talentName]);

  /** REMOVING FROM WATCHLIST */
  useEffect(() => {
    if (watchlistService?.removeUser?.success
      && !watchlistService?.removeUser?.fetching
      && talentName) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'success',
          content: `${talentName} ${BANNER_MESSAGE?.successRemove}`,
        },
      });
      resetNotification(true);
    }
    if (watchlistService?.removeUser?.error && !watchlistService?.removeUser?.fetching) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          content: BANNER_MESSAGE.error,
          variant: 'error',
        },
      });
      resetNotification(true);
    }
  }, [
    watchlistService?.removeUser?.success,
    watchlistService?.removeUser?.fetching,
    watchlistService?.removeUser?.error,
    talentName,
  ]);
  const handleWatchlistAction = (talent, fullName) => {
    if (!talent?.profile?.isWatched) {
      setTalentName(fullName);
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.added_talent_to_watchlist,
        properties: {
          talentId: talent?.profile?._id,
        },
      });
      dispatch({
        type: ActionTypes.watchlist.add.START,
        body: {
          userId: talent?.profile?._id,
        },
        userInfo: {
          _id: talent?.profile?._id,
          channelId,
          firstName: talent?.profile?.firstName,
          lastName: talent?.profile?.lastName,
          graduation: talent?.profile?.graduation,
          myShot: talent?.profile?.myShot,
        },
      });
    }
    if (talent?.profile?.isWatched) {
      setTalentName(fullName);
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.removed_talent_from_watchlist,
        properties: {
          talentId: talent?.profile?._id,
        },
      });
      dispatch({
        type: ActionTypes.watchlist.remove.START,
        body: {
          userId: talent?.profile?._id,
        },
        userInfo: {
          _id: talent?.profile?._id,
          channelId,
        },
      });
    }
  };

  const onPaginate = (inView): void => {
    if (channelService?.isLastPage || channelService?.fetching || !inView) return;
    dispatch({
      type: ActionTypes.discover.getChannel.START,
      id: channelId,
      body: {
        hint: channelService?.hint || {},
        ...!!Object.keys(filterService).length && {
          filters: filterService,
        },
      },
    });
  };

  const renderCards = () => (channelService?.results ?? []).map((item, index) => {
    if (!Object.keys(item).length) return null;

    const fullName = `${item?.profile?.firstName} ${item?.profile?.lastName}`;
    const videoSource = item?.video?.sources?.mp4_1080_square?.url
    || item?.video?.sources?.mp4?.url;

    const thumbnail = item?.video?.sources?.thumbnail_square?.url
    || item?.video?.sources?.thumbnail?.url;

    const thumbnailOptimized = item?.video?.sources?.thumbnail_optimized?.url || '';

    const TALENT_RESULT = [{
      headline: {
        title: {
          text: fullName,
          onClick: () => history.push(`/discover/talent/${item?.profile?._id}`, {
            name: channelService?.name,
            backLink: `/discover/channel/${channelService?._id}`,
          }),
          variant: 'text',
          small: true,
          type: 'heading',
        },
        squircleButton: {
          icon: {
            name: item?.profile?.isWatched ? 'checkmark-fill' : 'plus',
          },
          inverted: item?.profile?.isWatched,
          onClick: () => handleWatchlistAction(item, fullName),
        },
      },
      thumbnail: {
        uri: thumbnail || ZIYNX_LOGO,
      },
      thumbnail_optimized: {
        uri: thumbnailOptimized,
      },
      video: {
        onPlay: () => {
          dispatch({
            type: ActionTypes.analytics.logEvent.START,
            name: eventNames?.viewed_talent_video,
            properties: {
              videoType: 'statement',
              questionType: location?.state?.title,
              channelId,
              videoId: item?._id,
            },
          });
        },
        uri: videoSource || ' ',
      },
    }];

    return (
      <CardWrapper key={`video_${item?._id}_at_${index}`} lastChild={index === channelService?.results?.length - 1}>
        <Card
          variant="videoSquareGroup"
          data={TALENT_RESULT}
          viewport={viewport}
        />
      </CardWrapper>
    );
  });

  const leftSlot = (): React$Node => (
    <LeftSlotWrapper>
      <LinkWrapper>
        <Link variant="caret" onClick={() => history.push('/discover', { from: 'Channel' })} color={Colors.GrayDark}>
          Back to Discover
        </Link>
      </LinkWrapper>
      <HeadlineWrapper>
        <Headline
          variant="page"
          title={{
            text: location?.state?.title,
            color: Colors.GrayVeryDark,
          }}
          squircleButton={isNonDesktop && {
            icon: {
              name: 'more',
            },
          }}
          popover={isNonDesktop && {
            links: RIGHT_ACTION_LINK_ARRAY,
          }}
          {...isNonMobile && isNonDesktop && {
            textLink: RIGHT_ACTION_LINK_ARRAY,
          }}
        />
      </HeadlineWrapper>
    </LeftSlotWrapper>
  );

  const leftContent = (): React$Node => (
    <>
      {renderCards()}
      <InView as="div" onChange={onPaginate}>
        {!channelService?.isLastPage && (
          <SpinnerWrapper>
            <LoadingSpinner />
          </SpinnerWrapper>
        )}
      </InView>

    </>
  );

  const rightSlot = (): React$Node => {
    const WATCHLIST_DATA = (discoverService?.watchlist?.data ?? []).map((item) => ({
      label: {
        text: getGraduationInfo(item?.graduation),
        labelPosition: 'bottom',
      },
      text: `${item?.firstName} ${item?.lastName}`,
      color: Colors.GrayDark,
      imageSource: item?.myShot?.sources?.thumbnail_optimized?.url
      || item?.myShot?.sources?.thumbnail?.url,
      squircleText: getInitials(`${item?.firstName} ${item?.lastName}`),
      onClick: () => {
        history.push(`/discover/talent/${item?._id}`, {
          name: channelService?.name,
          backLink: `/discover/channel/${channelService?._id}`,
        });
      },
    })).filter((item, index) => index <= 2);

    /** FILTER DATA */
    const FILTER_RESULTS = getTotalFilters(Object.values(filterService ?? {}))
      .map((filter) => ({
        label: {
          text: filter?.name ? filter?.name : filter,
          color: Colors.GrayVeryDark,
        },
        hoverConfig: {
          iconRight: Colors.PrimaryBlue,
        },
        iconRight: {
          name: 'close',
          color: Colors.GrayVeryDark,
        },
        width: 'auto',
        onClick: () => {
          dispatch({
            type: ActionTypes.filters.remove,
            removeFromApplied: true,
            filters: {
              fieldId: findField(filterService, filter?.name ? filter?.name : filter),
              content: filter?.name ? filter?.name : filter,
            },
          });
        },
      }));

    return (
      <RightSlotWrapper>
        <FilterWrapper>
          <FilterHeadlineWrapper>
            <Headline
              title={{
                text: 'Filter',
                color: Colors.GrayDark,
              }}
              textLink={{
                children: !FILTER_TOTAL ? 'Edit' : `Edit (${FILTER_TOTAL})`,
                onClick: goToFilter,
              }}
            />
          </FilterHeadlineWrapper>
          <PillWrapper>
            {!FILTER_TOTAL ? (
              <Button
                variant="pill"
                label={{
                  text: 'No filter applied',
                  color: Colors.GrayDark,
                }}
                iconLeft={{
                  name: 'filter',
                  color: Colors.Gray,
                }}
                backgroundColor={Colors.GrayVeryLight}
                onClick={goToFilter}
              />
            ) : (
              <ButtonPillGroup
                pills={FILTER_RESULTS}
                stacked={false}
              />
            )}
          </PillWrapper>
        </FilterWrapper>
        <Card
          variant="listItemSquircle"
          headline={{
            title: {
              text: 'Your watchlist',
            },
            ...discoverService?.watchlist?.data?.length && {
              textLink: {
                children: `See all (${WATCHLIST_TOTAL > 99 ? `${WATCHLIST_TOTAL}+` : WATCHLIST_TOTAL})`,
                onClick: goToWatchlist,
              },
            },
          }}
          data={WATCHLIST_DATA}
        />
      </RightSlotWrapper>
    );
  };

  const getOffset = () => {
    if (viewport === 'mobileSmall' || viewport === 'mobile') {
      return '84px';
    }
    return '94px';
  };

  const stickyHeaderData = {
    textLinks: RIGHT_ACTION_LINK_ARRAY,
    popover: {
      links: RIGHT_ACTION_LINK_ARRAY,
    },
    title: { text: location?.state?.title },
    topOffset: getOffset(),
    squircleButton: {
      icon: {
        name: 'more',
      },
    },
    onClick: goToWatchlist, // For SquircleButton functionality
  };

  return (
    <>
      <Helmet>
        <title>{`${location?.state?.title ?? 'Discover'} | ZIYNX`}</title>
      </Helmet>
      <ContentContainer isVertical offsetTop={(isMobileSmall || viewport === 'mobile') ? 86 : 96}>
        <PageTemplate
          alwaysShowRightSlot
          leftSlot={leftSlot()}
          rightSlot={rightSlot()}
          leftContent={
            (!(channelService?.results ?? []).length && !channelService?.fetching) ? (
              <DiscoverEmptyState onClick={() => {
                dispatch({
                  type: ActionTypes.filters.removeAll,
                });
              }}
              />
            ) : leftContent()
          }
          stickyHeader={stickyHeaderData}
          rightSlotMarginLeft="40px"
        />

      </ContentContainer>
    </>
  );
};

export default (withAppManager(ChannelScreen): React$ComponentType<ManagedProps<Props>>);
